import { useEffect, useRef } from 'react';

// Escape-to-close + background scroll lock for modals.
//
// Before this, no modal in the app handled Escape at all — a grep for
// "Escape" across src/ returned zero hits — and none locked the page behind
// them, so the feed scrolled under an open sheet.
//
// It also parks a `data-modal-open` counter on <body>. usePullToRefresh binds
// at the document level and arms whenever a touch starts at scrollY <= 0, so
// without this a drag INSIDE a modal opened from the top of the feed would
// pull the feed down behind it.
//
// The count and the saved overflow are module-level, not per-hook: with two
// modals open, whichever unmounts LAST must restore the value from before the
// FIRST one opened. A per-instance snapshot gets that wrong — the inner modal
// would capture 'hidden' (already set by the outer one) and restore it.

let openCount = 0;
let savedOverflow = '';

export function useModalDismiss(onDismiss, { lockScroll = true } = {}) {
  // Held in a ref so the listener binds once and never restacks when the
  // caller passes a fresh closure each render.
  const handlerRef = useRef(onDismiss);
  useEffect(() => { handlerRef.current = onDismiss; }, [onDismiss]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') handlerRef.current?.();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    if (!lockScroll) return undefined;

    if (openCount === 0) {
      savedOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
    }
    openCount += 1;
    document.body.dataset.modalOpen = String(openCount);

    return () => {
      openCount = Math.max(0, openCount - 1);
      if (openCount === 0) {
        delete document.body.dataset.modalOpen;
        document.body.style.overflow = savedOverflow;
      } else {
        document.body.dataset.modalOpen = String(openCount);
      }
    };
  }, [lockScroll]);
}

export default useModalDismiss;
